import { useState } from 'react';
import { Button, Divider, Input, Popconfirm, Segmented, Select } from 'antd';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import type {
  CreditAccountCard,
  CreditAcquirer,
  CreditBank,
  CreditGrouping,
  CreditSettings,
} from '@deposits/shared';
import { Loader } from '../../components/Loader';
import {
  useCreateCreditAccountCard,
  useCreditAccountCards,
  useCreditSettings,
  useDeleteCreditEntity,
  useSaveCreditEntity,
  useUpdateCreditGrouping,
  useUpdateCreditIssuer,
} from '../../hooks/useCredit';

const onError = (err: Error) => toast.error(err.message || 'שגיאה בשמירת ההגדרות');

/**
 * Credit deposit settings for one location (or the whole box when it doesn't
 * manage locations — the server resolves the scope). Every change saves at once.
 */
export function CreditSettingsSection({ locationId }: { locationId: number }) {
  const { data: settings, isLoading } = useCreditSettings(locationId);
  const setGrouping = useUpdateCreditGrouping(locationId);

  if (isLoading || !settings) return <Loader />;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="text-small text-main-text mb-2">הפקדה לפי</div>
        <Segmented<CreditGrouping>
          block
          value={settings.grouping}
          onChange={(v) => setGrouping.mutate(v, { onError })}
          options={[
            { label: 'חברה מנפיקה', value: 'issuer' },
            { label: 'חברה סולקת', value: 'acquirer' },
          ]}
        />
        <div className="text-small text-secondary-text mt-2">
          {settings.grouping === 'issuer'
            ? 'החברה נקבעת לפי המותג שמגיע מ-Arbox (ויזה, ישראכרט, דיינרס…).'
            : 'החברה הסולקת לא מגיעה מ-Arbox. הגדירו את החברות הסולקות ושייכו אליהן כל חברה מנפיקה.'}
        </div>
      </div>

      <div>
        <div className="text-small text-main-text mb-2">בנקים</div>
        <BanksManager locationId={locationId} banks={settings.banks} />
      </div>

      {settings.grouping === 'issuer' ? (
        <IssuersTable locationId={locationId} settings={settings} />
      ) : (
        <>
          <div>
            <div className="text-small text-main-text mb-2">חברות סולקות</div>
            <AcquirersManager locationId={locationId} settings={settings} />
          </div>
          <IssuerMapping locationId={locationId} settings={settings} />
        </>
      )}
    </div>
  );
}

// ──────────────────────────────── Banks ─────────────────────────────────────

function BanksManager({ locationId, banks }: { locationId: number; banks: CreditBank[] }) {
  const save = useSaveCreditEntity(locationId);
  const del = useDeleteCreditEntity(locationId);
  const [editing, setEditing] = useState<CreditBank | 'new' | null>(null);
  const [name, setName] = useState('');
  const [ledger, setLedger] = useState('');

  const submit = () => {
    if (!name.trim() || !ledger.trim()) {
      toast.error('יש להזין שם בנק ולבחור כרטסת');
      return;
    }
    save.mutate(
      {
        entity: 'banks',
        id: editing && editing !== 'new' ? editing.id : undefined,
        body: { name: name.trim(), ledger_account: ledger.trim() },
      },
      { onSuccess: () => setEditing(null), onError },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {banks.map((b) =>
        editing !== 'new' && editing?.id === b.id ? null : (
          <ItemRow
            key={b.id}
            title={b.name}
            subtitle={`כרטסת ${b.ledger_account}`}
            onEdit={() => {
              setEditing(b);
              setName(b.name);
              setLedger(b.ledger_account);
            }}
            onDelete={() => del.mutate({ entity: 'banks', id: b.id }, { onError })}
            deleteTitle="למחוק את הבנק?"
          />
        ),
      )}
      {editing ? (
        <div className="border border-primary rounded p-3 bg-blue-bg flex flex-col gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="שם הבנק (לדוגמה: הפועלים – ראשי)" autoFocus />
          <LedgerCardSelect
            locationId={locationId}
            value={ledger || null}
            onChange={(v, card) => {
              setLedger(v ?? '');
              if (card && !name.trim()) setName(card.name);
            }}
            defaultName={name}
            allowClear={false}
          />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(null)}>ביטול</Button>
            <Button type="primary" onClick={submit} loading={save.isPending}>
              שמור
            </Button>
          </div>
        </div>
      ) : (
        <Button
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => {
            setEditing('new');
            setName('');
            setLedger('');
          }}
        >
          הוסף בנק
        </Button>
      )}
    </div>
  );
}

// ─────────────────────────────── Issuer mode ────────────────────────────────

function IssuersTable({ locationId, settings }: { locationId: number; settings: CreditSettings }) {
  const update = useUpdateCreditIssuer(locationId);
  if (settings.issuers.length === 0) {
    return <div className="text-small text-secondary-text">לא נמצאו תשלומי אשראי ב-Arbox בשנה האחרונה.</div>;
  }
  return (
    <div>
      <div className="text-small text-main-text mb-2">חברות מנפיקות</div>
      <div className="flex flex-col gap-2">
        {settings.issuers.map((i) => (
          <div key={i.name} className="border border-border rounded px-3 py-2 bg-white flex flex-col gap-2">
            <div className="font-medium">{i.name}</div>
            <div className="grid grid-cols-2 gap-2">
              <LedgerCardSelect
                locationId={locationId}
                value={i.ledger_account}
                onChange={(v) => update.mutate({ name: i.name, ledger_account: v }, { onError })}
                defaultName={i.name}
              />
              <BankSelect
                banks={settings.banks}
                value={i.default_bank_id}
                onChange={(v) => update.mutate({ name: i.name, default_bank_id: v }, { onError })}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────── Acquirer mode ───────────────────────────────

function AcquirersManager({ locationId, settings }: { locationId: number; settings: CreditSettings }) {
  const save = useSaveCreditEntity(locationId);
  const del = useDeleteCreditEntity(locationId);
  const [editing, setEditing] = useState<CreditAcquirer | 'new' | null>(null);
  const [name, setName] = useState('');
  const [ledger, setLedger] = useState('');
  const [bankId, setBankId] = useState<number | null>(null);

  const submit = () => {
    if (!name.trim()) {
      toast.error('יש להזין שם חברה');
      return;
    }
    save.mutate(
      {
        entity: 'acquirers',
        id: editing && editing !== 'new' ? editing.id : undefined,
        body: { name: name.trim(), ledger_account: ledger.trim() || null, default_bank_id: bankId },
      },
      { onSuccess: () => setEditing(null), onError },
    );
  };
  const bankName = (id: number | null) => settings.banks.find((b) => b.id === id)?.name;

  return (
    <div className="flex flex-col gap-2">
      {settings.acquirers.map((a) =>
        editing !== 'new' && editing?.id === a.id ? null : (
          <ItemRow
            key={a.id}
            title={a.name}
            subtitle={[
              a.ledger_account ? `כרטסת ${a.ledger_account}` : 'אין כרטסת',
              bankName(a.default_bank_id) ? `בנק: ${bankName(a.default_bank_id)}` : 'אין בנק ברירת מחדל',
            ].join(' · ')}
            warn={!a.ledger_account}
            onEdit={() => {
              setEditing(a);
              setName(a.name);
              setLedger(a.ledger_account ?? '');
              setBankId(a.default_bank_id);
            }}
            onDelete={() => del.mutate({ entity: 'acquirers', id: a.id }, { onError })}
            deleteTitle="למחוק את החברה? החברות המנפיקות ששויכו אליה יישארו ללא שיוך."
          />
        ),
      )}
      {editing ? (
        <div className="border border-primary rounded p-3 bg-blue-bg flex flex-col gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="שם החברה (לדוגמה: כאל)" autoFocus />
          <LedgerCardSelect
            locationId={locationId}
            value={ledger || null}
            onChange={(v, card) => {
              setLedger(v ?? '');
              if (card && !name.trim()) setName(card.name);
            }}
            defaultName={name}
          />
          <BankSelect banks={settings.banks} value={bankId} onChange={setBankId} />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(null)}>ביטול</Button>
            <Button type="primary" onClick={submit} loading={save.isPending}>
              שמור
            </Button>
          </div>
        </div>
      ) : (
        <Button
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => {
            setEditing('new');
            setName('');
            setLedger('');
            setBankId(null);
          }}
        >
          הוסף חברה סולקת
        </Button>
      )}
    </div>
  );
}

function IssuerMapping({ locationId, settings }: { locationId: number; settings: CreditSettings }) {
  const update = useUpdateCreditIssuer(locationId);
  if (settings.issuers.length === 0) return null;
  return (
    <div>
      <div className="text-small text-main-text mb-2">שיוך חברה מנפיקה ← חברה סולקת</div>
      <div className="flex flex-col gap-2">
        {settings.issuers.map((i) => (
          <div key={i.name} className="grid grid-cols-2 gap-2 items-center">
            <span className={i.acquirer_id == null ? 'text-error-red' : ''}>{i.name}</span>
            <Select<number>
              value={i.acquirer_id ?? undefined}
              onChange={(v) => update.mutate({ name: i.name, acquirer_id: v ?? null }, { onError })}
              allowClear
              placeholder="לא משויך"
              options={settings.acquirers.map((a) => ({ value: a.id, label: a.name }))}
              notFoundContent="הוסף חברה סולקת קודם"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────── Pieces ────────────────────────────────────

/** Leading zeros don't matter ("01031" = 1031), same as the server. */
function sameAccountNumber(a: string, b: string): boolean {
  return /^\d+$/.test(a) && /^\d+$/.test(b) && Number(a) === Number(b);
}

/**
 * Picks a ledger account (כרטסת) from the location's receipt cards in Arbox,
 * or creates a new card there. The value is the card's account number.
 */
function LedgerCardSelect({
  locationId,
  value,
  onChange,
  defaultName,
  allowClear = true,
}: {
  locationId: number;
  value: string | null;
  onChange: (v: string | null, card?: CreditAccountCard) => void;
  /** Prefills the name of a new card. */
  defaultName?: string;
  allowClear?: boolean;
}) {
  const { data: cards = [], isLoading, isError } = useCreditAccountCards(locationId);
  const create = useCreateCreditAccountCard(locationId);
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');

  const match = value ? cards.find((c) => sameAccountNumber(c.account_number, value)) : undefined;
  const options = cards.map((c) => ({ value: c.account_number, label: `${c.name} · ${c.account_number}` }));
  // Typed by hand before this list existed. The journal sync creates the card
  // in Arbox on the first report.
  if (value && !match && !isLoading) options.unshift({ value, label: `${value} · לא קיימת ב-Arbox` });

  const submitNew = () => {
    if (!newName.trim() || !/^\d+$/.test(newNumber.trim())) {
      toast.error('יש להזין שם ומספר כרטסת (ספרות בלבד)');
      return;
    }
    create.mutate(
      { name: newName.trim(), account_number: newNumber.trim() },
      {
        onSuccess: (card) => {
          onChange(card.account_number, card);
          setNewNumber('');
          toast.success(`הכרטסת ${card.name} נוצרה ב-Arbox`);
        },
        onError,
      },
    );
  };

  return (
    <Select<string>
      value={match?.account_number ?? value ?? undefined}
      onChange={(v) => onChange(v ?? null, cards.find((c) => c.account_number === v))}
      onOpenChange={(open) => open && setNewName(defaultName ?? '')}
      allowClear={allowClear}
      showSearch
      optionFilterProp="label"
      loading={isLoading}
      placeholder="כרטסת ב-Arbox"
      status={value ? undefined : 'warning'}
      options={options}
      notFoundContent={isError ? 'לא ניתן לטעון כרטסות מ-Arbox' : 'לא נמצאה כרטסת'}
      popupMatchSelectWidth={300}
      popupRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          <div className="text-small text-secondary-text px-2 mb-1">כרטסת חדשה ב-Arbox</div>
          <div className="flex gap-2 px-2 pb-1" onKeyDown={(e) => e.stopPropagation()}>
            <Input size="small" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="שם" />
            <Input
              size="small"
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              placeholder="מספר"
              dir="ltr"
              style={{ width: 90 }}
            />
            <Button size="small" type="primary" loading={create.isPending} onClick={submitNew}>
              צור
            </Button>
          </div>
        </>
      )}
    />
  );
}

function BankSelect({
  banks,
  value,
  onChange,
}: {
  banks: CreditBank[];
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <Select<number>
      value={value ?? undefined}
      onChange={(v) => onChange(v ?? null)}
      allowClear
      placeholder="בנק ברירת מחדל"
      options={banks.map((b) => ({ value: b.id, label: b.name }))}
      notFoundContent="הוסף בנק קודם"
    />
  );
}

function ItemRow({
  title,
  subtitle,
  warn,
  onEdit,
  onDelete,
  deleteTitle,
}: {
  title: string;
  subtitle: string;
  warn?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  deleteTitle: string;
}) {
  return (
    <div className="flex items-center justify-between border border-border rounded px-3 py-2 bg-white">
      <div>
        <div className="font-medium">{title}</div>
        <div className={`text-small ${warn ? 'text-error-red' : 'text-secondary-text'}`}>{subtitle}</div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onEdit} className="p-2 rounded hover:bg-grey-bg text-secondary-text hover:text-main-text">
          <FontAwesomeIcon icon={faPen} style={{ fontSize: 13 }} />
        </button>
        <Popconfirm title={deleteTitle} okText="מחק" cancelText="ביטול" onConfirm={onDelete}>
          <button className="p-2 rounded hover:bg-grey-bg text-error-red">
            <FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} />
          </button>
        </Popconfirm>
      </div>
    </div>
  );
}
